import { useId, type ReactNode, type SVGProps } from "react";
import Icon from "@ant-design/icons";
import type { CustomIconComponentProps } from "@ant-design/icons/lib/components/Icon";

/**
 * Los dos tipos de canal de WhatsApp (#513): el globo de `WhatsAppOutlined` con una insignia abajo a
 * la derecha — un QR para el canal por QR (`whatsapp`), el logo de Meta para la Cloud API
 * (`whatsapp_cloud`). Los colores son fijos, no heredan el del texto: se usan con el `Icon` de
 * antd para que respeten el `fontSize`.
 *
 * Los ids del mask y del degradé salen de `useId`: con varios íconos en la misma página, un id
 * repetido hace que uno tome la máscara del otro.
 */

// El path de `WhatsAppOutlined` de @ant-design/icons.
const GLOBO = (
  <>
    <path d="M713.5 599.9c-10.9-5.6-65.2-32.2-75.3-35.8-10.1-3.8-17.5-5.6-24.8 5.6-7.4 11.1-28.4 35.8-35 43.3-6.4 7.4-12.9 8.3-23.8 2.8-64.8-32.4-107.3-57.8-150-131.1-11.3-19.5 11.3-18.1 32.4-60.2 3.6-7.4 1.8-13.7-1-19.3-2.8-5.6-24.8-59.8-34-81.9-8.9-21.5-18.1-18.5-24.8-18.9-6.4-.4-13.7-.4-21.1-.4-7.4 0-19.3 2.8-29.4 13.7-10.1 11.1-38.6 37.8-38.6 92s39.5 106.7 44.9 114.1c5.6 7.4 77.7 118.6 188.4 166.5 70 30.2 97.4 32.8 132.4 27.6 21.3-3.2 65.2-26.6 74.3-52.5 9.1-25.8 9.1-47.9 6.4-52.5-2.7-4.9-10.1-7.7-21-13z" />
    <path d="M925.2 338.4c-22.6-53.7-55-101.9-96.3-143.3a444.35 444.35 0 00-143.3-96.3C630.6 75.7 572.2 64 512 64h-2c-60.6.3-119.3 12.3-174.5 35.9a445.35 445.35 0 00-142 96.5c-40.9 41.3-73 89.3-95.2 142.8-23 55.4-34.6 114.3-34.3 174.9A449.4 449.4 0 00112 714v152a46 46 0 0046 46h152.1A449.4 449.4 0 00510 960h2.1c59.9 0 118-11.6 172.7-34.3a444.48 444.48 0 00142.8-95.2c41.3-40.9 73.8-88.7 96.5-142 23.6-55.2 35.6-113.9 35.9-174.5.3-60.9-11.5-120-34.8-175.6zm-151.1 438C704 845.8 611 884 512 884h-1.7c-60.3-.3-120.2-15.3-173.1-43.5l-8.4-4.5H188V695.2l-4.5-8.4C155.3 633.9 140.3 574 140 513.7c-.4-99.7 37.7-193.3 107.6-263.8 69.8-70.5 163.1-109.5 262.8-109.9h1.7c50 0 98.5 9.7 144.2 28.9 44.6 18.7 84.6 45.6 119 80 34.3 34.3 61.3 74.4 80 119 19.4 46.2 29.1 95.2 28.9 145.8-.6 99.6-39.7 192.9-110.1 262.7z" />
  </>
);

/** Dónde va la insignia, en las unidades del viewBox del globo (64–960). */
interface Caja {
  x: number;
  size: number;
}

/**
 * El globo con la insignia abajo a la derecha. El `hueco` es lo que se le recorta al globo para
 * separarla (el "borde blanco"), dibujado en negro en las coordenadas de la insignia: un cuadrado
 * para el QR, el contorno del propio logo para Meta.
 */
const ConInsignia = ({
  caja,
  zoom,
  hueco,
  insignia,
  ...props
}: SVGProps<SVGSVGElement> & {
  caja: Caja;
  /** El viewBox de la insignia: el logo de Meta es apaisado y se acerca para que no se pierda. */
  zoom: string;
  hueco: ReactNode;
  insignia: (id: string) => ReactNode;
}) => {
  const id = useId();
  const enCaja = (contenido: ReactNode) => (
    <svg
      x={caja.x}
      y={caja.x}
      width={caja.size}
      height={caja.size}
      viewBox={zoom}
    >
      {contenido}
    </svg>
  );
  return (
    <svg
      viewBox="64 64 896 896"
      width="1em"
      height="1em"
      aria-hidden="true"
      {...props}
    >
      <defs>
        <mask id={`${id}-hueco`}>
          <rect x="0" y="0" width="1024" height="1024" fill="#fff" />
          {enCaja(hueco)}
        </mask>
      </defs>
      <g mask={`url(#${id}-hueco)`} fill="#25D366">
        <g transform="translate(64 64) scale(0.88) translate(-64 -64)">
          {GLOBO}
        </g>
      </g>
      {enCaja(insignia(id))}
    </svg>
  );
};

const WhatsAppQrSvg = (props: SVGProps<SVGSVGElement>) => (
  <ConInsignia
    {...props}
    caja={{ x: 400, size: 560 }}
    zoom="0 0 24 24"
    hueco={
      <rect x="-0.75" y="-0.75" width="25" height="25" rx="5.5" fill="#000" />
    }
    insignia={() => (
      <g fill="#434343">
        <path
          fillRule="evenodd"
          d="M0 0h10v10H0zM3 3v4h4V3zM14 0h10v10H14zM17 3v4h4V3zM0 14h10v10H0zM3 17v4h4v-4z"
        />
        <path d="M14 14h4v4h-4zM20 20h4v4h-4z" />
      </g>
    )}
  />
);

const META =
  "M12 11.4C10.4 8.4 9 6.2 7.1 6.2 4.6 6.2 2.8 9.8 2.8 13.2 2.8 15.9 4.3 17.6 6.4 17.6 8.8 17.6 10.2 15 12 11.4 13.8 8 15.2 6.2 17.2 6.2 19.8 6.2 21.4 9.8 21.4 13 21.4 15.8 20 17.6 17.9 17.6 15.4 17.6 13.6 14.4 12 11.4z";

// Sin caja cuadrada (#513): el hueco sigue el contorno del logo, así que no queda un fondo blanco
// alrededor y el logo puede ser más grande.
const WhatsAppMetaSvg = (props: SVGProps<SVGSVGElement>) => (
  <ConInsignia
    {...props}
    caja={{ x: 400, size: 560 }}
    zoom="1.2 1.2 21.6 21.6"
    hueco={
      <path
        d={META}
        fill="#000"
        stroke="#000"
        strokeWidth="4.9"
        strokeLinejoin="round"
      />
    }
    insignia={(id) => (
      <>
        <defs>
          <linearGradient id={`${id}-meta`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#0064E1" />
            <stop offset="1" stopColor="#0082FB" />
          </linearGradient>
        </defs>
        <path
          fill="none"
          stroke={`url(#${id}-meta)`}
          strokeWidth="3.1"
          strokeLinejoin="round"
          d={META}
        />
      </>
    )}
  />
);

/** Canal de WhatsApp por QR (`whatsapp`). */
export const WhatsAppQrIcon = (props: Partial<CustomIconComponentProps>) => (
  <Icon component={WhatsAppQrSvg} {...props} />
);

/** Canal de WhatsApp de Meta, la Cloud API (`whatsapp_cloud`). */
export const WhatsAppMetaIcon = (props: Partial<CustomIconComponentProps>) => (
  <Icon component={WhatsAppMetaSvg} {...props} />
);
